/**
 * Help articles: Markdown with front matter, read into data the Help screen renders.
 *
 * Pure on purpose -- no React, no DOM, no Vite -- so the same code the screen uses is what
 * `web/test/help-articles.test.ts` runs over every article file. A test that parsed the files
 * its own way would pass while the screen showed something else.
 *
 * It reads the SUBSET `STYLE.md` prescribes, not Markdown at large: `##` headings, paragraphs,
 * numbered and bulleted lists (nested, with wrapped lines), `> Screenshot: <name> — <caption>`,
 * other `>` notes, and inline **bold**, *italic*, `code` and links. A link whose target is an
 * article id, `[E10 Dates](E10)`, is a reference to that article. A heading may end in
 * `{#anchor}`, which becomes its id, so `/help/E23#v1-1` lands on it (What's new, F149).
 */

export type Audience = "everyone" | "pm" | "admin";
export const AUDIENCES: readonly Audience[] = ["everyone", "pm", "admin"];

/** The id letter each audience's articles carry: E1, P1, A1. */
export const ID_LETTER: Record<Audience, string> = { everyone: "E", pm: "P", admin: "A" };

/** An article id as written in front matter and links. */
export const ARTICLE_ID = /^[EPA]\d{1,3}$/;

/** A capture's file name in the crawl folders, e.g. `fp-01-home` or `sch-05-my-requests`. */
export const CAPTURE_NAME = /^[a-z0-9][a-z0-9._-]*$/i;

export type Inline =
  | { readonly t: "text"; readonly v: string }
  | { readonly t: "strong"; readonly children: readonly Inline[] }
  | { readonly t: "em"; readonly children: readonly Inline[] }
  | { readonly t: "code"; readonly v: string }
  /** `[label](E10)`: another article. */
  | { readonly t: "ref"; readonly id: string; readonly label: string }
  /** `[label](https://…)`: somewhere else. Any other target renders as its label. */
  | { readonly t: "link"; readonly href: string; readonly label: string };

export interface ListItem {
  text: string;
  /** Nested lists under this item. */
  children: Block[];
}

export type Block =
  | { readonly kind: "heading"; readonly level: number; readonly text: string; readonly anchor?: string }
  | { readonly kind: "para"; text: string }
  | { readonly kind: "list"; readonly ordered: boolean; readonly start: number; readonly items: ListItem[] }
  /** `name` is null for a line that names no capture, such as "not captured yet — …". */
  | { readonly kind: "shot"; readonly name: string | null; readonly caption: string }
  | { readonly kind: "note"; text: string };

export interface Article {
  readonly id: string;
  readonly title: string;
  readonly audience: Audience;
  readonly screens: readonly string[];
  readonly related: readonly string[];
  /** The folder the file was found in: `everyone`, `pm` or `admin`, if the file is right. */
  readonly folder: string;
  /** The file name, e.g. `E05-reading-a-task.md`. */
  readonly file: string;
  readonly blocks: readonly Block[];
  /** Everything a reader could search for, as plain text. */
  readonly text: string;
  /** The opening paragraph as plain text: the line under a title in a list. */
  readonly lede: string;
}

export class ArticleError extends Error {}

/** Splits `./articles/everyone/E05-x.md` (or a Windows path) into folder and file name. */
export function locate(path: string): { folder: string; file: string } {
  const parts = path.split(/[\\/]/).filter(Boolean);
  return { file: parts[parts.length - 1] ?? path, folder: parts[parts.length - 2] ?? "" };
}

// ── Front matter ────────────────────────────────────────────────────────────

function frontMatter(raw: string, where: string): { fields: Map<string, string>; body: string } {
  const text = raw.replace(/^﻿/, "").replace(/\r\n?/g, "\n");
  if (!text.startsWith("---\n")) throw new ArticleError(`${where}: no front matter (the file must start with ---)`);
  const end = text.indexOf("\n---", 4);
  if (end < 0) throw new ArticleError(`${where}: front matter is not closed with ---`);
  const fields = new Map<string, string>();
  for (const line of text.slice(4, end).split("\n")) {
    if (line.trim() === "") continue;
    const m = /^([A-Za-z_][\w-]*):\s*(.*)$/.exec(line);
    if (!m) throw new ArticleError(`${where}: front matter line is not "key: value": ${line}`);
    fields.set(m[1] ?? "", (m[2] ?? "").trim());
  }
  const after = text.indexOf("\n", end + 4);
  return { fields, body: after < 0 ? "" : text.slice(after + 1) };
}

function scalar(fields: Map<string, string>, key: string, where: string): string {
  const v = fields.get(key);
  if (v === undefined || v === "") throw new ArticleError(`${where}: front matter has no ${key}`);
  return v.replace(/^(["'])(.*)\1$/, "$2");
}

function list(fields: Map<string, string>, key: string, where: string): string[] {
  const v = fields.get(key);
  if (v === undefined) throw new ArticleError(`${where}: front matter has no ${key}`);
  const m = /^\[(.*)\]$/.exec(v);
  if (!m) throw new ArticleError(`${where}: ${key} must be a list like [a, b]`);
  return (m[1] ?? "")
    .split(",")
    .map((s) => s.trim().replace(/^(["'])(.*)\1$/, "$2"))
    .filter((s) => s !== "");
}

// ── Blocks ──────────────────────────────────────────────────────────────────

const HEADING = /^(#{2,4})\s+(.*)$/;
/** `## V1.1 - 30 Oct 2026 {#v1-1}`: the anchor a link can land on. */
const ANCHOR = /\s*\{#([a-z0-9-]+)\}\s*$/;
const SHOT = /^>\s?Screenshot:\s*(.*)$/i;
const QUOTE = /^>\s?(.*)$/;
const MARKER = /^(\s*)(?:(\d+)[.)]|[-*])\s+(.*)$/;

interface Marker {
  indent: number;
  ordered: boolean;
  start: number;
  text: string;
}

function marker(line: string): Marker | null {
  const m = MARKER.exec(line);
  if (!m) return null;
  return {
    indent: (m[1] ?? "").length,
    ordered: m[2] !== undefined,
    start: m[2] ? Number(m[2]) : 1,
    text: (m[3] ?? "").trim(),
  };
}

const indentOf = (line: string): number => line.length - line.trimStart().length;
const blank = (line: string | undefined): boolean => line === undefined || line.trim() === "";
const startsBlock = (line: string): boolean => HEADING.test(line) || QUOTE.test(line);

/** `> Screenshot: fp-01-home — the header` → name and caption. */
export function parseShot(rest: string): { name: string | null; caption: string } {
  const cut = /\s+[—–]\s+/.exec(rest);
  const head = (cut ? rest.slice(0, cut.index) : rest).trim();
  const tail = cut ? rest.slice(cut.index + cut[0].length).trim() : "";
  if (CAPTURE_NAME.test(head)) return { name: head, caption: tail };
  // "not captured yet — the panel open": no capture to show, but the caption still says what goes there.
  return { name: null, caption: tail || head };
}

/**
 * A list starting at `lines[i]`, whose items sit at `indent`. Lines indented deeper than an item
 * continue it, or start a list nested in it; a blank line ends the list unless more of it follows.
 */
function parseList(lines: string[], start: number, indent: number): { block: Block; next: number } {
  const first = marker(lines[start] ?? "");
  const ordered = first?.ordered ?? false;
  const items: ListItem[] = [];
  let i = start;

  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (blank(line)) {
      // A blank line inside a list: carry on only if the list (or this item) continues after it.
      let j = i + 1;
      while (j < lines.length && blank(lines[j])) j++;
      const ahead = lines[j];
      if (ahead === undefined) break;
      const m = marker(ahead);
      const continues = m ? m.indent > indent || (m.indent === indent && m.ordered === ordered) : indentOf(ahead) > indent;
      if (!continues) break;
      i = j;
      continue;
    }
    const m = marker(line);
    const last = items[items.length - 1];
    if (m && m.indent < indent) break;
    if (m && m.indent === indent) {
      if (m.ordered !== ordered) break;
      items.push({ text: m.text, children: [] });
      i++;
      continue;
    }
    if (m && last) {
      const nested = parseList(lines, i, m.indent);
      last.children.push(nested.block);
      i = nested.next;
      continue;
    }
    if (!last || startsBlock(line)) break;
    if (indentOf(line) <= indent && blank(lines[i - 1])) break;
    // A wrapped line: it continues the deepest item open at this point.
    let target = last;
    for (;;) {
      const child = target.children[target.children.length - 1];
      if (!child || child.kind !== "list") break;
      const deeper = child.items[child.items.length - 1];
      if (!deeper) break;
      target = deeper;
    }
    target.text += " " + line.trim();
    i++;
  }
  return { block: { kind: "list", ordered, start: first?.start ?? 1, items }, next: i };
}

export function parseBlocks(body: string): Block[] {
  const lines = body.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (blank(line)) {
      i++;
      continue;
    }
    const h = HEADING.exec(line);
    if (h) {
      const text = (h[2] ?? "").trim();
      const anchor = ANCHOR.exec(text)?.[1];
      blocks.push({
        kind: "heading",
        level: (h[1] ?? "##").length,
        text: anchor ? text.replace(ANCHOR, "") : text,
        ...(anchor ? { anchor } : {}),
      });
      i++;
      continue;
    }
    const shot = SHOT.exec(line);
    if (shot) {
      blocks.push({ kind: "shot", ...parseShot(shot[1] ?? "") });
      i++;
      continue;
    }
    if (QUOTE.test(line)) {
      const parts: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i] ?? "") && !SHOT.test(lines[i] ?? "")) {
        parts.push((QUOTE.exec(lines[i] ?? "")?.[1] ?? "").trim());
        i++;
      }
      blocks.push({ kind: "note", text: parts.join(" ").trim() });
      continue;
    }
    const m = marker(line);
    if (m) {
      const { block, next } = parseList(lines, i, m.indent);
      blocks.push(block);
      i = next;
      continue;
    }
    const parts: string[] = [];
    while (i < lines.length && !blank(lines[i]) && !startsBlock(lines[i] ?? "") && !marker(lines[i] ?? "")) {
      parts.push((lines[i] ?? "").trim());
      i++;
    }
    blocks.push({ kind: "para", text: parts.join(" ") });
  }
  return blocks;
}

// ── Inline ──────────────────────────────────────────────────────────────────

/**
 * One pass with alternation, as `RichText.tsx` does it, so an asterisk inside code or a link
 * cannot be taken by a later rule. Bold and italic are parsed again inside, so a link in bold
 * still links.
 */
const INLINE = /(\*\*(?:[^*]|\*(?!\*))+?\*\*)|(\*[^*\s](?:[^*]*?[^*\s])?\*)|(`[^`]+`)|(\[[^\]]+\]\([^)\s]+\))/g;

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  for (const m of text.matchAll(INLINE)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ t: "text", v: text.slice(last, at) });
    const token = m[0];
    if (m[1]) out.push({ t: "strong", children: parseInline(token.slice(2, -2)) });
    else if (m[2]) out.push({ t: "em", children: parseInline(token.slice(1, -1)) });
    else if (m[3]) out.push({ t: "code", v: token.slice(1, -1) });
    else {
      const cut = token.indexOf("](");
      const label = token.slice(1, cut);
      const target = token.slice(cut + 2, -1).trim();
      if (ARTICLE_ID.test(target)) out.push({ t: "ref", id: target, label });
      else if (/^https?:\/\//i.test(target)) out.push({ t: "link", href: target, label });
      else out.push({ t: "text", v: label });
    }
    last = at + token.length;
  }
  if (last < text.length) out.push({ t: "text", v: text.slice(last) });
  return out;
}

export function plainInline(text: string): string {
  return parseInline(text)
    .map(function flat(n: Inline): string {
      switch (n.t) {
        case "text":
        case "code":
          return n.v;
        case "ref":
        case "link":
          return n.label;
        case "strong":
        case "em":
          return n.children.map(flat).join("");
      }
    })
    .join("");
}

/** Every inline piece of text in the blocks, in reading order. */
function eachText(blocks: readonly Block[], visit: (text: string) => void): void {
  for (const b of blocks) {
    if (b.kind === "list") {
      for (const item of b.items) {
        visit(item.text);
        eachText(item.children, visit);
      }
    } else if (b.kind === "shot") visit(b.caption);
    else visit(b.text);
  }
}

/** The article ids an article's text links to, `[..](E10)`, in order, without repeats. */
export function referencedIds(blocks: readonly Block[]): string[] {
  const ids: string[] = [];
  const walk = (nodes: readonly Inline[]): void => {
    for (const n of nodes) {
      if (n.t === "ref" && !ids.includes(n.id)) ids.push(n.id);
      if (n.t === "strong" || n.t === "em") walk(n.children);
    }
  };
  eachText(blocks, (text) => walk(parseInline(text)));
  return ids;
}

// ── Articles ────────────────────────────────────────────────────────────────

export function parseArticle(path: string, raw: string): Article {
  const { folder, file } = locate(path);
  const where = `${folder}/${file}`;
  const { fields, body } = frontMatter(raw, where);

  const id = scalar(fields, "id", where);
  if (!ARTICLE_ID.test(id)) throw new ArticleError(`${where}: id "${id}" is not like E5, P12 or A3`);
  const audience = scalar(fields, "audience", where);
  if (!AUDIENCES.some((a) => a === audience)) {
    throw new ArticleError(`${where}: audience "${audience}" is not one of ${AUDIENCES.join(", ")}`);
  }
  const blocks = parseBlocks(body);
  const searchable: string[] = [];
  eachText(blocks, (text) => searchable.push(plainInline(text)));
  const first = blocks.find((b) => b.kind === "para");

  return {
    id,
    title: scalar(fields, "title", where),
    audience: audience as Audience,
    screens: list(fields, "screens", where),
    related: list(fields, "related", where),
    folder,
    file,
    blocks,
    text: searchable.join("\n"),
    lede: first && first.kind === "para" ? plainInline(first.text) : "",
  };
}

/** True for an article about the resource scheduler: every screen it names is a `scheduler-*` one. */
export function isScheduler(a: Pick<Article, "screens">): boolean {
  return a.screens.length > 0 && a.screens.every((s) => s.startsWith("scheduler-"));
}

const idNumber = (id: string): number => Number(id.slice(1));

/** Everyone first, then PMs, then admins; Fieldpath before the scheduler; then by number. */
export function compareArticles(a: Article, b: Article): number {
  return (
    AUDIENCES.indexOf(a.audience) - AUDIENCES.indexOf(b.audience) ||
    Number(isScheduler(a)) - Number(isScheduler(b)) ||
    idNumber(a.id) - idNumber(b.id) ||
    a.id.localeCompare(b.id)
  );
}

export interface Library {
  readonly articles: readonly Article[];
  readonly byId: ReadonlyMap<string, Article>;
  /** Files that failed to parse. Shown nowhere in the app; the test fails on any. */
  readonly errors: readonly string[];
  /** The articles whose `screens` list this screen, in reading order. */
  forScreen(screen: string): Article[];
}

export function buildLibrary(files: readonly { path: string; raw: string }[]): Library {
  const articles: Article[] = [];
  const errors: string[] = [];
  for (const f of files) {
    try {
      articles.push(parseArticle(f.path, f.raw));
    } catch (e) {
      errors.push(e instanceof Error ? e.message : String(e));
    }
  }
  articles.sort(compareArticles);
  const byId = new Map<string, Article>();
  for (const a of articles) if (!byId.has(a.id)) byId.set(a.id, a);
  return {
    articles,
    byId,
    errors,
    forScreen: (screen) => articles.filter((a) => a.screens.includes(screen)),
  };
}

// ── Search ──────────────────────────────────────────────────────────────────

export interface Hit {
  readonly article: Article;
  /** A stretch of the article around the first match, split so the match can be marked. */
  readonly snippet: { readonly before: string; readonly match: string; readonly after: string } | null;
}

/**
 * Every word of the query must appear in the article's id, title or text. Title matches rank
 * first, because someone typing "dates" wants the Dates article before every article that
 * mentions a date.
 */
export function search(lib: Library, query: string): Hit[] {
  const words = query.toLowerCase().split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) return [];
  const scored: { hit: Hit; score: number }[] = [];
  for (const article of lib.articles) {
    const title = `${article.id} ${article.title}`.toLowerCase();
    const text = article.text.toLowerCase();
    if (!words.every((w) => title.includes(w) || text.includes(w))) continue;
    const inTitle = words.filter((w) => title.includes(w)).length;
    const count = words.reduce((n, w) => n + text.split(w).length - 1, 0);
    scored.push({ hit: { article, snippet: snippet(article.text, words) }, score: inTitle * 1000 + count });
  }
  return scored.sort((a, b) => b.score - a.score || compareArticles(a.hit.article, b.hit.article)).map((s) => s.hit);
}

function snippet(text: string, words: readonly string[]): Hit["snippet"] {
  const flat = text.replace(/\s+/g, " ");
  const lower = flat.toLowerCase();
  let at = -1;
  let word = "";
  for (const w of words) {
    const i = lower.indexOf(w);
    if (i >= 0 && (at < 0 || i < at)) {
      at = i;
      word = w;
    }
  }
  if (at < 0) return null;
  const from = Math.max(0, flat.lastIndexOf(" ", Math.max(0, at - 70)) + 1);
  const to = Math.min(flat.length, at + word.length + 110);
  return {
    before: (from > 0 ? "…" : "") + flat.slice(from, at),
    match: flat.slice(at, at + word.length),
    after: flat.slice(at + word.length, to) + (to < flat.length ? "…" : ""),
  };
}
